import type { AxiosError } from 'axios';

/**
 * Error messages in Turkish (default locale for RaporGor)
 */
export const ERROR_MESSAGES = {
  unauthorized: 'Oturum süreniz doldu, lütfen tekrar giriş yapın',
  forbidden: 'Bu kaynağa erişim yetkiniz yok',
  notFound: 'İstenen kaynak bulunamadı',
  tooManyRequests: 'Çok fazla istek gönderildi, lütfen bekleyin',
  serverError: 'Sunucu hatası oluştu, lütfen daha sonra tekrar deneyin',
  networkError: 'Bağlantı hatası, internet bağlantınızı kontrol edin',
  unknownError: 'Bilinmeyen bir hata oluştu',
  validationError: 'Girdiğiniz bilgilerde hata var',
} as const;

/**
 * Error severity levels
 */
export type ErrorSeverity = 'error' | 'warning' | 'info';

/**
 * Processed error information
 */
export interface ProcessedError {
  message: string;
  severity: ErrorSeverity;
  status?: number;
  shouldReport: boolean; // Should be reported to Sentry
}

/**
 * Error handler options
 */
export interface ErrorHandlerOptions {
  onError?: (error: ProcessedError) => void;
  reportToSentry?: (error: AxiosError, context?: ApiErrorContext) => void;
  silent?: boolean; // Don't show any messages
}

/**
 * Context for error reporting
 */
export interface ApiErrorContext {
  url?: string;
  method?: string;
  userId?: number;
  firmId?: number;
  extra?: Record<string, unknown>;
}

/**
 * Process an Axios error into a user-friendly format
 */
export function processApiError(error: AxiosError): ProcessedError {
  const status = error.response?.status;

  // Network error (no response)
  if (!error.response) {
    return {
      message: ERROR_MESSAGES.networkError,
      severity: 'error',
      shouldReport: true,
    };
  }

  switch (status) {
    case 400:
      return {
        message: ERROR_MESSAGES.validationError,
        severity: 'warning',
        status,
        shouldReport: false,
      };
    case 401:
      return {
        message: ERROR_MESSAGES.unauthorized,
        severity: 'warning',
        status,
        shouldReport: false,
      };
    case 403:
      return {
        message: ERROR_MESSAGES.forbidden,
        severity: 'warning',
        status,
        shouldReport: false,
      };
    case 404:
      return {
        message: ERROR_MESSAGES.notFound,
        severity: 'warning',
        status,
        shouldReport: false,
      };
    case 429:
      return {
        message: ERROR_MESSAGES.tooManyRequests,
        severity: 'warning',
        status,
        shouldReport: false,
      };
    case 500:
    case 502:
    case 503:
    case 504:
      return {
        message: ERROR_MESSAGES.serverError,
        severity: 'error',
        status,
        shouldReport: true,
      };
    default:
      return {
        message: ERROR_MESSAGES.unknownError,
        severity: 'error',
        status,
        shouldReport: true,
      };
  }
}

/**
 * Handle an API error
 * - Processes the error
 * - Calls the onError callback (for showing toast/message)
 * - Reports to Sentry if needed
 */
export function handleApiError(
  error: AxiosError,
  options?: ErrorHandlerOptions,
  context?: ApiErrorContext
): ProcessedError {
  const processed = processApiError(error);

  // Skip if silent mode
  if (options?.silent) {
    return processed;
  }

  // Call error callback (for UI notification)
  if (options?.onError) {
    options.onError(processed);
  }

  // Report to Sentry if needed
  if (processed.shouldReport && options?.reportToSentry) {
    options.reportToSentry(error, context);
  }

  return processed;
}

/**
 * Extract error message from API response
 */
export function extractErrorMessage(error: AxiosError): string {
  const data = error.response?.data as { message?: string; error?: string } | undefined;
  return data?.message || data?.error || error.message;
}
